import { AfterViewInit, Component, Input, ViewChild } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { User } from '../Models/User';
import { Router } from '@angular/router';
import { Beneficiary } from '../Models/Beneficiary';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { AdminBeneficiaryDialogComponent } from '../admin-beneficiary-dialog/admin-beneficiary-dialog.component';

@Component({
  selector: 'app-admin-beneficiaries',
  templateUrl: './admin-beneficiaries.component.html',
  styleUrl: './admin-beneficiaries.component.css',
  standalone: false
})
export class AdminBeneficiariesComponent implements AfterViewInit {

  @Input({ required: true }) loggedInUser?: User;
  private loggedInUserTag = "";

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  beneficiaries: Beneficiary[] = [];
  tableBeneficiaries = new MatTableDataSource<Beneficiary>([]);
  displayedColumns: string[] = ['Id', 'BeneficiarySourceURN', 'Source.Name', 'Name', 'Aliases', 'AddressLine1', 'Postcode', 'IsClient', 'Activities', 'Active', 'Actions'];
  showAllFlag: boolean = false;

  pageSize = 50;
  currentPage = 0;

  get pagedBeneficiaries(): Beneficiary[] {
    const start = this.currentPage * this.pageSize;
    return this.beneficiaries.slice(start, start + this.pageSize);
  }

  pageChanged(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.currentPage = event.pageIndex;
    localStorage.setItem('beneficiary-page-size', String(event.pageSize));
  }


  // ---------- Named (Beneficiary) ----------
  beneficiarySearchQuery = {
    beneficiaryName: '',
    beneficiaryAliasName: '',
    beneficiaryURN: '',
    address: '',
    postcode: ''
  };

  beneficiarySearchResults: any[] = [];
  selectedBeneficiary: any = null;
  constructor(private http: HttpClient, private router: Router, private dialog: MatDialog) { }

  ngOnInit() {
    const saved = localStorage.getItem('beneficiary-page-size');
    if (saved) this.pageSize = +saved;

    this.loggedInUserTag = `${this.loggedInUser?.lastName}, ${this.loggedInUser?.firstName}`;
    this.getBeneficiaries();
  }

  ngAfterViewInit() {
    if (this.paginator) {
      this.paginator.pageSize = this.pageSize;
    }
  }

  applyFilter(value: string) {
    //this.filterText = value;
    //this.tableBeneficiaries.filter = value.trim().toLowerCase();
  }

  getBeneficiaries() {
    const params = new HttpParams().set('user', this.loggedInUserTag);

    this.http.get<Beneficiary[]>('/charitycentral/getallbeneficiaries', { params })
      .subscribe(result => {
        this.beneficiaries = result.map(r => this.normalizeBeneficiaryForUI(r));
        this.tableBeneficiaries.data = this.beneficiaries;
        this.currentPage = 0;
        if (this.paginator) this.paginator.firstPage();
      });
  }

  private normalizeBeneficiaryForUI(b: any): Beneficiary {
    return {
      ...b,
      aliases: Array.isArray(b.aliases) ? b.aliases.join(', ') : (b.aliases ?? ''),
      previousNames: Array.isArray(b.previousNames) ? b.previousNames.join(', ') : (b.previousNames ?? ''),
      isExpanded: false,
      isSelected: false, // 👈 NEW
      source: b.source ?? { id: 0, name: '', code: '' }
    } as Beneficiary;
  }


  // Helper used by template to display comma-separated strings as array
  splitToArray(val: any): string[] {
    if (!val) return [];
    if (Array.isArray(val)) return val.map(v => String(v).trim()).filter(Boolean);
    if (typeof val === 'string') return val.split(',').map(s => s.trim()).filter(Boolean);
    return [];
  }

  addBeneficiary() {
    const ref = this.dialog.open(AdminBeneficiaryDialogComponent, {
      width: '900px',
      maxHeight: '90vh',
      data: {
        beneficiary: {
          id: 0,
          name: '',
          beneficiarySourceURN: '',
          active: true,
          isClient: true,
          source: { id: 1, name: 'Manual Entry', code: 'MAN' }
        }
      }
    });

    ref.afterClosed().subscribe(result => {
      if (result) {
        this.saveBeneficiary(result);
      }
    });
  }


  editBeneficiary(beneficiary: Beneficiary) {
    const ref = this.dialog.open(AdminBeneficiaryDialogComponent, {
      width: '900px',
      maxHeight: '90vh',
      data: { beneficiary }
    });

    ref.afterClosed().subscribe(result => {
      if (result) {
        this.saveBeneficiary(result);
      }
    });
  }


  cancelEdit(beneficiary: any) {
    beneficiary.isEdit = false;
    beneficiary.isExpanded = false;
    this.showAllFlag = false;
    this.onBeneficiarySearch();
  }

  saveBeneficiary(beneficiary: any) {
    const params = new HttpParams()
      .set('user', this.loggedInUserTag);

    // Normalize helper: ensure value is an array of strings
    const normalizeArray = (val: any): string[] => {
      if (!val) return [];
      if (Array.isArray(val)) return val.map(v => String(v));
      if (typeof val === 'string') return val.split(',').map(s => s.trim()).filter(Boolean);
      return [];
    };

    const beneficiaryPayload: any = {
      // copy fields from the bound model
      ...beneficiary,
      active: beneficiary.active ?? true,
      isClient: beneficiary.isClient ?? false,
      aliases: normalizeArray(beneficiary.aliases),
      source: beneficiary.source ?? { id:1, name: 'Manual Entry', code: 'MAN' },
      previousNames: normalizeArray(beneficiary.previousNames),
    };

    if (beneficiary.id ===0) {
      // CREATE
      this.http.post<number>('/charitycentral/addbeneficiary', beneficiaryPayload, { params })
        .subscribe(() => this.onBeneficiarySearch());
    } else {
      // UPDATE
      this.http.post<boolean>('/charitycentral/updatebeneficiary', beneficiaryPayload, { params })
        .subscribe(() => this.onBeneficiarySearch());
    }
  }

  deleteBeneficiary(beneficiary: any) {
    const ref = this.dialog.open(ConfirmDialogComponent, {
      data: { title: 'Delete Beneficiary', message: 'Are you sure you want to delete this beneficiary?', confirmText: 'Yes', cancelText: 'No' }
    });

    ref.afterClosed().subscribe(result => {
      if (result) {
        const params = new HttpParams()
          .set('beneficiaryId', beneficiary.id)
          .set('user', this.loggedInUserTag);

        this.http.post<boolean>('/charitycentral/deletebeneficiary', null, { params })
          .subscribe(success => {
            if (success) {
              this.beneficiaries = this.beneficiaries.filter(b => b.id !== beneficiary.id);
              this.tableBeneficiaries.data = this.beneficiaries;
            }
          });
      }
    });
  }

  // ---------------- BENEFICIARY SEARCH ----------------
  onBeneficiarySearch() {
    const q = this.beneficiarySearchQuery;
    const hasQuery = q.beneficiaryName || q.beneficiaryAliasName || q.beneficiaryURN || q.address || q.postcode;

    if (!hasQuery) {
      this.getBeneficiaries();
      return;
    }

    const params = new HttpParams()
      .set('beneficiaryname', q.beneficiaryName || '')
      .set('beneficiaryaliasname', q.beneficiaryAliasName || '')
      .set('beneficiaryurn', q.beneficiaryURN || '')
      .set('address', q.address || '')
      .set('postcode', q.postcode || '');

    this.http.get<any[]>('/charitycentral/getbeneficiaries', { params })
      .subscribe(results => {
        this.beneficiarySearchResults = results;
        this.beneficiaries = results.map(r => this.normalizeBeneficiaryForUI(r));
        this.tableBeneficiaries.data = this.beneficiaries;
        this.currentPage = 0;
        if (this.paginator) this.paginator.firstPage();
      });
  }

  onBeneficiarySearchKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.onBeneficiarySearch();
    }
  }

  openPanel(beneficiary: any) {
    this.beneficiaries.forEach(b => {
      b.isExpanded = false;
      b.isSelected = false;
    });

    beneficiary.isExpanded = true;
    beneficiary.isSelected = true;
  }

  closePanel(beneficiary: any) {
    beneficiary.isExpanded = false;
    beneficiary.isSelected = false;
  }


  initAliases(beneficiary: any) {
    beneficiary.aliasArray = beneficiary.aliases
      ? beneficiary.aliases.split(',').map((a: string) => a.trim())
      : [];

    beneficiary.newAlias = '';
  }

  addAlias(beneficiary: any) {
    if (!beneficiary.newAlias) return;

    beneficiary.aliasArray.push(beneficiary.newAlias.trim());
    beneficiary.newAlias = '';

    beneficiary.aliases = beneficiary.aliasArray.join(', ');
  }

  removeAlias(beneficiary: any, index: number) {
    beneficiary.aliasArray.splice(index, 1);
    beneficiary.aliases = beneficiary.aliasArray.join(', ');
  }

  formatAddress(b: any): string {
    const lines = [
      b.addressLine1,
      b.addressLine2,
      b.addressLine3,
      b.addressLine4
    ].filter(Boolean);

    const address = lines.join(', ');
    return b.postcode ? `${address}, ${b.postcode}` : address;
  }



}
